import {inject, Injectable, Injector, NgZone} from '@angular/core';
import {HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {firstValueFrom, Observable, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay} from 'rxjs/operators';
import {ExpiredTokenError, SpringLoginResponse, SpringRestService} from '@aiknow-srl/aik-ngx-http';
import {EventData} from '@aiknow-srl/aik-ngx-eventbus';
import {BaseService} from '../BaseService';
import {GlobalModelService} from '../GlobalModel.service';
import {LogoutEvent} from '../../events/LogoutEvent';
import {GrpcAuthorizationOPEService} from '../ope/grpc/authorizationOPE.service';

interface TokenResponse {
  access_token: string;
  refresh_token?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthStateService extends BaseService {

  private static readonly SESSION_TOKEN_KEY = 'session_token';
  private static readonly ACCESS_TOKEN_KEY = 'access_token';
  private static readonly LEGACY_TOKEN_KEYS = ['refresh_token', 'accessToken', 'refreshToken'];
  private static readonly PROACTIVE_REFRESH_LEAD_MS = 60_000;
  private static readonly PROACTIVE_REFRESH_MIN_DELAY_MS = 5_000;

  // HttpBackend skips every interceptor: login and refresh must never carry a Bearer token or be retried
  private http = new HttpClient(inject(HttpBackend));
  private be = inject(SpringRestService);
  private gmService = inject(GlobalModelService);
  private injector = inject(Injector);
  private zone = inject(NgZone);
  private authorizationService = inject(GrpcAuthorizationOPEService);

  private accessToken?: string;
  private sessionToken?: string;
  private temporaryToken?: string;
  private refresh$?: Observable<string>;
  private proactiveRefreshTimer?: ReturnType<typeof setTimeout>;
  private restoring = false;

  constructor() {
    super();
    AuthStateService.LEGACY_TOKEN_KEYS.forEach(key => localStorage.removeItem(key));
    this.sessionToken = localStorage.getItem(AuthStateService.SESSION_TOKEN_KEY) ?? undefined;
    const storedAccessToken = localStorage.getItem(AuthStateService.ACCESS_TOKEN_KEY);
    const storedExpiry = storedAccessToken ? this.readExpiry(storedAccessToken) : undefined;
    if (this.sessionToken && storedAccessToken && storedExpiry !== undefined && storedExpiry > Date.now()) {
      this.setAccessToken(storedAccessToken);
    } else {
      localStorage.removeItem(AuthStateService.ACCESS_TOKEN_KEY);
    }
  }

  isLoggedIn(): boolean {
    return !!this.accessToken;
  }

  hasSession(): boolean {
    return !!this.sessionToken;
  }

  getAccessToken(): string | undefined {
    return this.accessToken;
  }

  login(username: string, password: string, rememberMe: boolean, hashPassword = true): Promise<SpringLoginResponse> {
    const body = {username, password: hashPassword ? this.be.hashPassword(password) : password};
    const headers = this.jsonHeaders().set('Authorization', 'Basic Y2xpZW50OnNlY3JldA==');
    return firstValueFrom(this.http.post<TokenResponse>(this.gmService.composeUrl() + '/login', body, {headers})).then(
      tokens => {
        this.setSession(tokens, rememberMe);
        return new SpringLoginResponse(true);
      },
      error => {
        throw this.toLoginResponse(error);
      });
  }

  /**
   * Single-flight per tab: concurrent callers share the same in-flight request and its outcome.
   * Fails with ExpiredTokenError when the session is no longer valid (state is already cleared and
   * the user redirected), or with the HttpErrorResponse when the backend was unreachable.
   */
  refresh(): Observable<string> {
    const sessionToken = this.sessionToken;
    if (!sessionToken) {
      return throwError(() => new ExpiredTokenError());
    }
    if (!this.refresh$) {
      const refresh$: Observable<string> = this.http.post<TokenResponse>(this.gmService.composeUrl() + '/refreshToken',
        {refresh_token: sessionToken}, {headers: this.jsonHeaders()}).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 0) {
            return throwError(() => error);
          }
          if (this.sessionToken === sessionToken) {
            this.expireSession();
          }
          return throwError(() => new ExpiredTokenError());
        }),
        map(tokens => {
          // Logged out (or in as someone else) while the refresh was in flight
          if (this.sessionToken !== sessionToken) {
            throw new ExpiredTokenError();
          }
          this.setAccessToken(tokens.access_token);
          return tokens.access_token;
        }),
        finalize(() => {
          if (this.refresh$ === refresh$) {
            this.refresh$ = undefined;
          }
        }),
        shareReplay({bufferSize: 1, refCount: false})
      );
      this.refresh$ = refresh$;
    }
    return this.refresh$;
  }

  /** Called before routing: without remember-me, or once the stored access token expired, a reload only has the session token. */
  restoreSession(): Promise<void> {
    if (this.accessToken || !this.sessionToken) {
      return Promise.resolve();
    }
    this.restoring = true;
    return firstValueFrom(this.refresh()).then(() => undefined, () => undefined)
      .finally(() => this.restoring = false);
  }

  /** The next intercepted request is sent with this token instead of the session's access token. */
  useTemporaryToken(token: string): void {
    this.temporaryToken = token;
  }

  consumeTemporaryToken(): string | undefined {
    const token = this.temporaryToken;
    this.temporaryToken = undefined;
    return token;
  }

  logout(): void {
    if (this.sessionToken) {
      // Fire and forget: the BE answers OK even for an unknown or already closed session
      this.http.post(this.gmService.composeUrl() + '/logout', {i: {refresh_token: this.sessionToken}},
        {headers: this.jsonHeaders()}).subscribe({error: () => undefined});
    }
    this.extEventBus.emit(new EventData(LogoutEvent.NAME, null));
    this.clear();
    this.injector.get(Router).navigateByUrl('');
  }

  clear(): void {
    this.accessToken = undefined;
    this.sessionToken = undefined;
    this.temporaryToken = undefined;
    this.refresh$ = undefined;
    this.cancelProactiveRefresh();
    // The impersonation JWT is not tied to the session: it would outlive the logout otherwise
    this.authorizationService.stopImpersonation();
    document.documentElement.style.removeProperty('--impersonation-banner-height');
    localStorage.removeItem(AuthStateService.SESSION_TOKEN_KEY);
    localStorage.removeItem(AuthStateService.ACCESS_TOKEN_KEY);
  }

  cleanupServiceFields(): void {
    // doNothing
  }

  private expireSession(): void {
    if (this.restoring) {
      // Before the initial navigation: the router lands on the login page by itself
      this.clear();
      return;
    }
    this.extEventBus.emit(new EventData(LogoutEvent.NAME, null));
    this.clear();
    this.injector.get(Router).navigate([''], {queryParams: {showExpiredTokenError: true}});
  }

  private setSession(tokens: TokenResponse, rememberMe: boolean): void {
    this.sessionToken = tokens.refresh_token ?? undefined;
    if (rememberMe && this.sessionToken) {
      localStorage.setItem(AuthStateService.SESSION_TOKEN_KEY, this.sessionToken);
    } else {
      localStorage.removeItem(AuthStateService.SESSION_TOKEN_KEY);
      localStorage.removeItem(AuthStateService.ACCESS_TOKEN_KEY);
    }
    this.setAccessToken(tokens.access_token);
  }

  private setAccessToken(token: string): void {
    this.accessToken = token;
    // Another tab may have stored a different session since: pairing our token with it would mix users on reload
    if (this.sessionToken && localStorage.getItem(AuthStateService.SESSION_TOKEN_KEY) === this.sessionToken) {
      localStorage.setItem(AuthStateService.ACCESS_TOKEN_KEY, token);
    }
    this.scheduleProactiveRefresh(token);
  }

  private scheduleProactiveRefresh(token: string): void {
    this.cancelProactiveRefresh();
    const exp = this.readExpiry(token);
    if (exp === undefined || !this.sessionToken) {
      return;
    }
    const delay = Math.max(exp - Date.now() - AuthStateService.PROACTIVE_REFRESH_LEAD_MS,
      AuthStateService.PROACTIVE_REFRESH_MIN_DELAY_MS);
    // Outside the zone, or the pending timer keeps the app permanently unstable
    this.zone.runOutsideAngular(() => {
      this.proactiveRefreshTimer = setTimeout(() => {
        this.zone.run(() => this.refresh().subscribe({error: () => undefined}));
      }, delay);
    });
  }

  private cancelProactiveRefresh(): void {
    if (this.proactiveRefreshTimer !== undefined) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = undefined;
    }
  }

  private readExpiry(token: string): number | undefined {
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const exp = JSON.parse(atob(payload)).exp;
      return typeof exp === 'number' ? exp * 1000 : undefined;
    } catch {
      return undefined;
    }
  }

  private jsonHeaders(): HttpHeaders {
    let headers = new HttpHeaders({'Content-Type': 'application/json', Accept: 'application/json'});
    const language = this.be.getLanguage();
    if (language !== undefined) {
      headers = headers.set('Accept-Language', language);
    }
    return headers;
  }

  private toLoginResponse(error: any): SpringLoginResponse {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string') {
        return new SpringLoginResponse(false, error.status, error.error);
      }
      if (typeof error.error === 'object' && !!error.error?.error && !!error.error?.message) {
        return new SpringLoginResponse(false, error.status, error.error.message);
      }
      return new SpringLoginResponse(false, error.status, undefined);
    }
    return new SpringLoginResponse(false, 200, typeof error === 'string' ? error : undefined);
  }
}
